import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Film, Clock, Calendar } from "lucide-react";

const empty = { name: "", description: "", video_count: 1, price: 0, seconds_per_video: 30, validity_months: 1, active: true };

export default function AdminPackages() {
  const { t, money } = useLang();
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/packages").then((r) => setPacks(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty }); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || "", video_count: p.video_count, price: p.price, seconds_per_video: p.seconds_per_video, validity_months: p.validity_months, active: p.active }); };

  const save = async () => {
    const payload = {
      name: form.name, description: form.description, video_count: Number(form.video_count), price: Number(form.price),
      seconds_per_video: Number(form.seconds_per_video), validity_months: Number(form.validity_months), active: form.active,
    };
    try {
      if (editing) { await api.put(`/packages/${editing.id}`, payload); toast.success(t("pkg.updated")); }
      else { await api.post("/packages", payload); toast.success(t("pkg.created")); }
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm(t("pkg.del_confirm"))) return;
    await api.delete(`/packages/${p.id}`); toast.success(t("pkg.deleted")); load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="ch-fade-up space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("pkg.eyebrow")}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.packages")}</h1>
        </div>
        <Button data-testid="new-package-button" onClick={openNew} className="rounded-sm bg-black hover:bg-neutral-800">
          <Plus className="mr-2 h-4 w-4" /> {t("pkg.new")}
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {packs.map((p) => (
          <div key={p.id} data-testid={`package-card-${p.id}`} className={`group relative rounded-sm border p-6 transition-colors ${p.active ? "border-neutral-200 hover:border-black" : "border-dashed border-neutral-200 opacity-60"}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-display text-xl font-bold">{p.name}</h3>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button data-testid={`edit-package-${p.id}`} onClick={() => openEdit(p)} className="rounded-sm p-1.5 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-package-${p.id}`} onClick={() => del(p)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <p className="mt-4 font-display text-4xl font-extrabold tracking-tight">{money(p.price)}</p>
            <p className="text-sm text-neutral-500">{money(p.price_per_video)} {t("pkg.per_video")}</p>
            {p.description && <p data-testid={`package-desc-${p.id}`} className="mt-3 whitespace-pre-line text-sm text-neutral-600">{p.description}</p>}
            <ul className="mt-5 space-y-2 border-t border-neutral-100 pt-4 text-sm text-neutral-600">
              <li className="flex items-center gap-2"><Film className="h-4 w-4 text-neutral-400" />{p.video_count} {t("pkg.count_label")}</li>
              <li className="flex items-center gap-2"><Clock className="h-4 w-4 text-neutral-400" />{p.seconds_per_video}s {t("pkg.seconds_label")}</li>
              <li className="flex items-center gap-2"><Calendar className="h-4 w-4 text-neutral-400" />{t("pkg.validity_label")} {p.validity_months} {t("pkg.months")}</li>
            </ul>
            {!p.active && <span className="mt-4 inline-block text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("pkg.inactive")}</span>}
          </div>
        ))}
        {packs.length === 0 && <p className="text-sm text-neutral-400">{t("pkg.empty")}</p>}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="package-form-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? t("pkg.edit") : t("pkg.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>{t("pkg.name")}</Label><Input data-testid="package-name" value={form.name} onChange={set("name")} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>{t("pkg.description")}</Label><Textarea data-testid="package-description" value={form.description} onChange={set("description")} placeholder={t("pkg.description_ph")} rows={3} className="rounded-sm" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>{t("pkg.count")}</Label><Input data-testid="package-count" type="number" min="1" value={form.video_count} onChange={set("video_count")} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>{t("common.price")} (€)</Label><Input data-testid="package-price" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>{t("pkg.seconds")}</Label><Input data-testid="package-seconds" type="number" min="1" value={form.seconds_per_video} onChange={set("seconds_per_video")} className="rounded-sm" /></div>
                <div className="space-y-1.5"><Label>{t("pkg.validity")}</Label><Input data-testid="package-validity" type="number" min="1" value={form.validity_months} onChange={set("validity_months")} className="rounded-sm" /></div>
              </div>
              <div className="flex items-center justify-between rounded-sm border border-neutral-200 p-3">
                <Label>{t("pkg.active")}</Label>
                <Switch data-testid="package-active" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
              </div>
              {Number(form.video_count) > 0 && (
                <p className="text-sm text-neutral-500">{t("pkg.price_per_video")} <span className="font-semibold text-black">{money(Number(form.price) / Number(form.video_count))}</span></p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button data-testid="save-package-button" onClick={save} className="rounded-sm bg-black hover:bg-neutral-800">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
